/**
 * Copy bindings. LEAD-OWNED.
 *
 * Document copy must never embed plan facts as literal text — a geo or budget
 * change would strand stale mentions all over the narrative. Instead, copy
 * carries {{tokens}} that resolve from Proposal state at render time, so one
 * underlying value serves every mention (screen, print, and PDF alike).
 *
 * Unknown tokens render as-is (visible, greppable) rather than vanishing.
 * Null facts resolve to an explicit pending marker — unknown stays unknown.
 */

import type { Budget, PrimaryGoal, Proposal } from './types.js';
import { NATIONAL_PLACE_NAME, areaDisplayNames, joinNames, nationalSummary } from './geography.js';

const GOAL_LABELS: Record<PrimaryGoal, string> = {
  brand_awareness: 'Brand Awareness',
  lead_generation: 'Lead Generation',
  sales_conversion: 'Sales Conversion',
  customer_retention: 'Customer Retention',
  market_expansion: 'Market Expansion',
  product_launch: 'Product Launch',
};

const PENDING = '—';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseIsoDate(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/** Adds `durationMonths` to `startIso` and steps back one day — an inclusive flight end. */
function deriveFlightEnd(startIso: string, durationMonths: number): string {
  const start = parseIsoDate(startIso);
  if (!start) return startIso;
  const date = new Date(Date.UTC(start.y, start.m - 1 + Math.max(1, durationMonths), start.d));
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/** The flight's end date: explicit if set, else derived from start + duration. Null when there is no start to derive from. */
function effectiveFlightEnd(budget: Budget): string | null {
  if (!budget.startDate) return null;
  return budget.endDate ?? deriveFlightEnd(budget.startDate, budget.durationMonths);
}

/** "Oct 1, 2025" */
function formatFlightDate(iso: string): string | null {
  const parsed = parseIsoDate(iso);
  if (!parsed) return null;
  return `${MONTHS[parsed.m - 1]} ${parsed.d}, ${parsed.y}`;
}

/**
 * "Oct–Dec 2025" (spans months, same year); "Oct 2025" (same month, collapses
 * the range); "Nov 2025–Jan 2026" (spans years, each end carries its own year).
 */
function formatFlightRange(startIso: string, endIso: string): string | null {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end) return null;
  if (start.y === end.y && start.m === end.m) return `${MONTHS[start.m - 1]} ${start.y}`;
  if (start.y === end.y) return `${MONTHS[start.m - 1]}–${MONTHS[end.m - 1]} ${start.y}`;
  return `${MONTHS[start.m - 1]} ${start.y}–${MONTHS[end.m - 1]} ${end.y}`;
}

/** "3 months" / "1 month". durationMonths always has a value (default 12), so this never goes pending. */
function formatFlightMonths(durationMonths: number): string {
  const n = Math.round(durationMonths);
  return `${n} month${n === 1 ? '' : 's'}`;
}

function money(value: number | null): string {
  return value === null ? PENDING : `$${Math.round(value).toLocaleString('en-US')}`;
}

function count(value: number | null): string {
  return value === null ? PENDING : Math.round(value).toLocaleString('en-US');
}

function humanizeProductId(productId: string): string {
  return productId
    .split('_')
    .map((w) => (w === 'ctv' ? 'CTV' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function geoSummary(proposal: Proposal): string {
  const geo = proposal.geography;
  const areaLabels = geo.areas.map((a) => a.label).filter(Boolean);
  if (geo.strategy === 'national') return nationalSummary(geo.areas);
  const around =
    areaLabels.length > 0
      ? areaLabels.join(', ')
      : (proposal.advertiser.locations[0]?.label ?? null);
  if (geo.strategy === 'store_radius' && geo.storeRadiusMiles !== null) {
    return around
      ? `a ${geo.storeRadiusMiles}-mile radius around ${around}`
      : `a ${geo.storeRadiusMiles}-mile radius around each location`;
  }
  if (areaLabels.length > 0) return joinNames(areaDisplayNames(geo.areas));
  return '(geography pending)';
}

/**
 * The full token vocabulary. Every entry resolves purely from Proposal state.
 */
export function bindingValue(key: string, proposal: Proposal): string | null {
  switch (key) {
    case 'advertiser.name':
      return proposal.advertiser.name ?? '(advertiser pending)';
    case 'advertiser.domain':
      return proposal.advertiser.domain ?? PENDING;
    case 'advertiser.industry':
      return proposal.advertiser.industry ?? PENDING;
    case 'objective.goal':
      return proposal.objective.primaryGoal
        ? GOAL_LABELS[proposal.objective.primaryGoal]
        : '(goal pending)';
    case 'objective.conversionEvent':
      return proposal.objective.conversionEvent ?? PENDING;
    case 'geo.summary':
      return geoSummary(proposal);
    case 'geo.radiusMiles':
      return proposal.geography.storeRadiusMiles === null
        ? PENDING
        : String(proposal.geography.storeRadiusMiles);
    case 'geo.areas': {
      if (proposal.geography.strategy === 'national') return NATIONAL_PLACE_NAME;
      const labels = areaDisplayNames(proposal.geography.areas);
      if (labels.length > 0) return joinNames(labels);
      return '(geography pending)';
    }
    case 'budget.monthly':
      return money(proposal.budget.monthly);
    case 'budget.annual':
      return money(proposal.budget.annual);
    case 'budget.durationMonths':
      return String(proposal.budget.durationMonths);
    case 'flight.dates': {
      const end = effectiveFlightEnd(proposal.budget);
      if (!proposal.budget.startDate || !end) return PENDING;
      return formatFlightRange(proposal.budget.startDate, end) ?? PENDING;
    }
    case 'flight.start':
      return proposal.budget.startDate ? (formatFlightDate(proposal.budget.startDate) ?? PENDING) : PENDING;
    case 'flight.end': {
      const end = effectiveFlightEnd(proposal.budget);
      return end ? (formatFlightDate(end) ?? PENDING) : PENDING;
    }
    case 'flight.months':
      return formatFlightMonths(proposal.budget.durationMonths);
    case 'audience.size':
      return proposal.audience.estimatedPeople === null
        ? '(audience size pending)'
        : count(proposal.audience.estimatedPeople.value);
    case 'audience.description':
      return proposal.audience.description ?? '(audience pending)';
    case 'plan.channels': {
      const names = proposal.mediaPlan.channels.map((c) => humanizeProductId(c.productId));
      return names.length > 0 ? names.join(', ') : '(media plan pending)';
    }
    case 'plan.channelCount':
      return String(proposal.mediaPlan.channels.length);
    case 'forecast.reach':
      return proposal.forecast.totalReach === null
        ? '(reach pending)'
        : count(proposal.forecast.totalReach);
    case 'forecast.frequency':
      return proposal.forecast.avgFrequency === null
        ? '(frequency pending)'
        : `${proposal.forecast.avgFrequency}×`;
    default: {
      // Per-channel budget: {{plan.<productId>.monthly}}
      const perChannel = /^plan\.([a-z0-9_]+)\.monthly$/.exec(key);
      if (perChannel) {
        const line = proposal.mediaPlan.channels.find((c) => c.productId === perChannel[1]);
        return line ? money(line.monthlyBudget) : PENDING;
      }
      return null; // unknown token — caller leaves it visible
    }
  }
}

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/** Resolve every {{token}} in a copy string against proposal state. */
export function resolveBindings(text: string, proposal: Proposal): string {
  return text.replace(TOKEN_RE, (raw, key: string) => bindingValue(key, proposal) ?? raw);
}

/** Every `{{token}}` key referenced in `text`, in order, duplicates included — does not check whether any of them resolve. */
export function extractTokens(text: string): string[] {
  return [...text.matchAll(TOKEN_RE)].map((m) => m[1]!);
}

/**
 * K1a: the static half of the vocabulary — every key `bindingValue` resolves
 * that isn't the dynamic per-channel `plan.<productId>.monthly` pattern.
 * `availableBindings` below adds one dynamic entry per planned channel.
 */
const STATIC_BINDING_KEYS = [
  'advertiser.name',
  'advertiser.domain',
  'advertiser.industry',
  'objective.goal',
  'objective.conversionEvent',
  'geo.summary',
  'geo.radiusMiles',
  'geo.areas',
  'budget.monthly',
  'budget.annual',
  'budget.durationMonths',
  'flight.dates',
  'flight.start',
  'flight.end',
  'flight.months',
  'audience.size',
  'audience.description',
  'plan.channels',
  'plan.channelCount',
  'forecast.reach',
  'forecast.frequency',
] as const;

const PENDING_PHRASE_RE = /^\(.*pending.*\)$/i;
/** "0", "$0", "0%", "0×" and similar — a resolved-but-zero value the renderer shows in place of a real fact. */
const ZERO_ISH_RE = /^\$?0(?:\.0+)?[%×x]?$/i;

/**
 * True for anything `bindingValue` (or a renderer) shows in place of a real
 * fact: the `—` pending marker (and bare runs of hyphen/en/em dash), an empty
 * string, "TBD", a "(… pending)" phrase, or a zero-ish value. Used by
 * `availableBindings` to decide which tokens a copy worker may actually rely
 * on — a binding that currently prints one of these is not "available", even
 * though the key itself is real.
 */
export function isPlaceholderBindingValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  if (/^[-‐-―]+$/.test(trimmed)) return true; // '-', en dash, em dash, etc., alone
  if (/^tbd$/i.test(trimmed)) return true;
  if (PENDING_PHRASE_RE.test(trimmed)) return true;
  if (ZERO_ISH_RE.test(trimmed)) return true;
  return false;
}

export interface AvailableBinding {
  key: string;
  /** The token's current rendered value. */
  value: string;
  /** `value.length` — how many characters this token costs wherever it's used. */
  length: number;
}

/**
 * K1a: the subset of `BINDING_VOCABULARY` that currently resolves to a real
 * value for this proposal, each with its rendered value and that value's
 * character length — so a copy worker (and `build-deck.ts`'s validation) can
 * see exactly what a token will cost before writing it, instead of guessing.
 * Excludes anything `isPlaceholderBindingValue` (the fact isn't known yet) —
 * those tokens render visibly on the page as "(pending)"/"—" text, which is
 * worse than the literal a rep can at least fill in, so copy must not lean on
 * them until the underlying fact is set.
 */
export function availableBindings(proposal: Proposal): AvailableBinding[] {
  const dynamicKeys = proposal.mediaPlan.channels.map((c) => `plan.${c.productId}.monthly`);
  const out: AvailableBinding[] = [];
  for (const key of [...STATIC_BINDING_KEYS, ...dynamicKeys]) {
    const value = bindingValue(key, proposal);
    if (value === null || isPlaceholderBindingValue(value)) continue;
    out.push({ key, value, length: value.length });
  }
  return out;
}

/**
 * Facts that MUST be written as bindings, never literals, in agent-authored
 * copy: dollar amounts and mile-radius phrases. Returns the offending
 * literals ([] when clean). Tokens themselves never match — they carry no
 * digits or $ before resolution.
 */
export function findUnboundFacts(text: string): string[] {
  const offenders: string[] = [];
  const moneyRe = /\$\s?\d[\d,]*(?:\.\d+)?[kKmM]?/g;
  const milesRe = /\b\d+(?:\.\d+)?[- ]?miles?\b/gi;
  for (const re of [moneyRe, milesRe]) {
    for (const match of text.matchAll(re)) offenders.push(match[0]);
  }
  return offenders;
}

/** One-line vocabulary reference, used in tool errors and the system prompt. */
export const BINDING_VOCABULARY =
  '{{advertiser.name}} {{advertiser.domain}} {{advertiser.industry}} {{objective.goal}} ' +
  '{{objective.conversionEvent}} {{geo.summary}} {{geo.radiusMiles}} {{geo.areas}} ' +
  '{{budget.monthly}} {{budget.annual}} {{budget.durationMonths}} ' +
  '{{flight.dates}} {{flight.start}} {{flight.end}} {{flight.months}} {{audience.size}} ' +
  '{{audience.description}} {{plan.channels}} {{plan.channelCount}} ' +
  '{{plan.<productId>.monthly}} {{forecast.reach}} {{forecast.frequency}}';
